import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Layers, RefreshCw, MoreVertical, Pencil, Stethoscope, FlaskConical, Landmark, FileText,
  ThumbsDown, ThumbsUp, AlertTriangle, Mail, Phone as PhoneIcon, CalendarDays, UserCircle, ArrowRightLeft,
} from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Textarea } from '@/components/ui/Textarea'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { DropdownMenu, type DropdownItem } from '@/components/ui/DropdownMenu'
import { ProviderViewModal, type ProviderViewMode } from './ProviderViewModal'
import {
  CredentialingProvidersService,
  type KanbanColumn, type KanbanCard,
} from '@/services/modules/CredentialingService'
import { UsersService } from '@/services/modules/UsersService'
import { useAuthStore } from '@/stores/authStore'
import { useAuthority } from '@/hooks/useAuthority'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'

type ViewMode = ProviderViewMode

export function CredentialingKanbanPage() {
  const nav = useNavigate()
  const [cols, setCols] = useState<KanbanColumn[]>([])
  const [loading, setLoading] = useState(false)
  const [dragId, setDragId] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<KanbanCard | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectSaving, setRejectSaving] = useState(false)
  const [approving, setApproving] = useState<KanbanCard | null>(null)
  const [approveSaving, setApproveSaving] = useState(false)
  const [view, setView] = useState<{ id: string; name: string; mode: ViewMode } | null>(null)
  // Transferência de titularidade — somente admins.
  const me = useAuthStore((s) => s.user)
  const isAdmin = me?.type === 'ADMINISTRADOR'
  const { can } = useAuthority()
  const canEdit = can('credentialing.providers.edit')
  const canMove = can('credentialing.providers.move_step')
  const canApprove = can('credentialing.providers.approve')
  const canReject = can('credentialing.providers.reject')
  const [transferring, setTransferring] = useState<KanbanCard | null>(null)
  const [transferUserId, setTransferUserId] = useState('')
  const [transferSaving, setTransferSaving] = useState(false)
  const [transferUsers, setTransferUsers] = useState<{ id: string; name: string }[]>([])
  // Filtro de usuários — visível só para admins/permissão. Colaborador vê só os próprios.
  const [userFilter, setUserFilter] = useState('')
  const [canFilterByUser, setCanFilterByUser] = useState(false)
  const [users, setUsers] = useState<{ id: string; name: string }[]>([])

  const load = useCallback(async (uid: string) => {
    setLoading(true)
    try {
      const { columns, canFilterByUser } = await CredentialingProvidersService.kanban(uid)
      setCols(columns)
      setCanFilterByUser(canFilterByUser)
    }
    catch { toast.error('Erro ao carregar kanban.') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load(userFilter) }, [load, userFilter])

  // Carrega a lista de usuários (com credenciamento em andamento) para o filtro.
  useEffect(() => {
    if (!canFilterByUser) return
    CredentialingProvidersService.kanbanUsers().then(setUsers).catch(() => setUsers([]))
  }, [canFilterByUser])

  // Usuários destino da transferência (admin/colaborador) — só para admins.
  useEffect(() => {
    if (!isAdmin) return
    UsersService.byType(['ADMINISTRADOR', 'COLABORADOR'])
      .then((list) => setTransferUsers(list.map((u) => ({ id: u.id, name: u.name }))))
      .catch(() => setTransferUsers([]))
  }, [isAdmin])

  async function drop(targetStepId: string) {
    if (!dragId || !canMove) return
    setCols(prev => {
      const next = prev.map(c => ({ ...c, providers: c.providers.filter(p => p.id !== dragId) }))
      const provider = prev.flatMap(c => c.providers).find(p => p.id === dragId)
      if (provider) {
        const target = next.find(c => c.id === targetStepId)
        if (target) target.providers = [...target.providers, provider]
      }
      return next
    })
    setDragOver(null); setDragId(null)
    try { await CredentialingProvidersService.updateStep(dragId, targetStepId); toast.success('Etapa atualizada.') }
    catch { toast.error('Erro ao mover. Recarregando'); load(userFilter) }
  }

  async function confirmApprove() {
    if (!approving) return
    setApproveSaving(true)
    try {
      await CredentialingProvidersService.approve(approving.id)
      toast.success({ title: 'Credenciado aceito', description: `${approving.name} saiu do kanban (status: Aceito).` })
      setApproving(null)
      load(userFilter)
    } catch { toast.error('Erro ao aceitar.') }
    finally { setApproveSaving(false) }
  }
  function openReject(card: KanbanCard) { setRejecting(card); setRejectReason('') }
  async function confirmReject() {
    if (!rejecting) return
    if (rejectReason.trim().length < 3) {
      toast.error({ title: 'Justificativa obrigatória', description: 'Descreva o motivo da rejeição (mín. 3 caracteres).' })
      return
    }
    setRejectSaving(true)
    try {
      await CredentialingProvidersService.reject(rejecting.id, rejectReason.trim())
      toast.success({ title: 'Credenciado rejeitado', description: rejecting.name })
      setRejecting(null); load(userFilter)
    } catch { toast.error('Erro ao rejeitar.') }
    finally { setRejectSaving(false) }
  }

  function openTransfer(card: KanbanCard) { setTransferring(card); setTransferUserId('') }
  async function confirmTransfer() {
    if (!transferring) return
    if (!transferUserId) {
      toast.error({ title: 'Selecione o usuário', description: 'Escolha o administrador ou colaborador de destino.' })
      return
    }
    setTransferSaving(true)
    try {
      await CredentialingProvidersService.transfer(transferring.id, transferUserId)
      const dest = transferUsers.find((u) => u.id === transferUserId)?.name ?? 'novo usuário'
      toast.success({ title: 'Credenciado transferido', description: `${transferring.name} → ${dest}` })
      setTransferring(null); load(userFilter)
    } catch (e) {
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao transferir.')
    } finally { setTransferSaving(false) }
  }

  function menuItems(card: KanbanCard): DropdownItem[] {
    const items: DropdownItem[] = [
      { key: 'edit', label: canEdit ? 'Ver e editar' : 'Ver', icon: <Pencil size={14} />, onClick: () => nav(`/credentialing/providers/${card.id}/editar`) },
      { key: 'spec', label: 'Ver especialidades', icon: <Stethoscope size={14} />, onClick: () => setView({ id: card.id, name: card.name, mode: 'specialties' }) },
      { key: 'test', label: 'Ver exames', icon: <FlaskConical size={14} />, onClick: () => setView({ id: card.id, name: card.name, mode: 'tests' }) },
    ]
    if (card.requiredBankDetails) {
      items.push({ key: 'bank', label: 'Dados bancários', icon: <Landmark size={14} />, onClick: () => setView({ id: card.id, name: card.name, mode: 'bank' }) })
    }
    items.push({ key: 'contract', label: 'Contrato', icon: <FileText size={14} />, onClick: () => setView({ id: card.id, name: card.name, mode: 'contract' }) })
    // Transferir titularidade — somente admins.
    if (isAdmin) {
      items.push({ key: 'transfer', label: 'Transferir', icon: <ArrowRightLeft size={14} />, divider: true, onClick: () => openTransfer(card) })
    }
    if (canReject) {
      items.push({ key: 'reject', label: 'Rejeitar', icon: <ThumbsDown size={14} />, danger: true, divider: true, onClick: () => openReject(card) })
    }
    return items
  }

  return (
    <>
      <PageHeader title="Kanban de credenciamento" subtitle="Arraste os cartões entre as etapas"
        crumbs={[{ label: 'Credenciamento' }, { label: 'Kanban' }]}
        actions={
          <div className="flex items-center gap-2">
            {canFilterByUser && (
              <Select
                wrapperClassName="w-56"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                options={users.map(u => ({ value: u.id, label: u.name }))}
                placeholder="Todos os colaboradores"
                searchPlaceholder="Buscar colaborador"
              />
            )}
            <Button variant="secondary" iconLeft={<RefreshCw size={14} />} onClick={() => load(userFilter)} loading={loading}>Recarregar</Button>
          </div>
        }
      />

      <div className="flex gap-3 overflow-x-auto pb-3">
        {cols.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center w-full py-14 text-gray-400">
            <Layers size={32} className="mb-2 opacity-40" />
            <p className="text-sm">Crie etapas em <strong>Credenciamento → Etapas</strong> para começar.</p>
          </div>
        )}
        {cols.map(col => (
          <div
            key={col.id}
            onDragOver={(e) => { if (!canMove) return; e.preventDefault(); setDragOver(col.id) }}
            onDragLeave={() => setDragOver(prev => prev === col.id ? null : prev)}
            onDrop={() => drop(col.id)}
            className={cn(
              'w-80 shrink-0 bg-gray-50 rounded-md ring-1 ring-gray-200 p-3 flex flex-col',
              dragOver === col.id && 'ring-brand-400 bg-brand-50/40',
            )}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">{col.step}</h3>
              <Badge variant="gray">{col.providers.length}</Badge>
            </div>
            <div className="flex-1 space-y-2 min-h-[80px]">
              {col.providers.map(p => (
                <KanbanCardView
                  key={p.id}
                  card={p}
                  menuItems={menuItems(p)}
                  draggable={canMove}
                  onDragStart={() => setDragId(p.id)}
                  onDragEnd={() => { setDragId(null); setDragOver(null) }}
                  onApprove={canApprove ? () => setApproving(p) : undefined}
                />
              ))}
              {col.providers.length === 0 && (
                <div className="text-[11px] text-gray-400 text-center py-6 border-2 border-dashed border-gray-200 rounded-md">
                  {canMove ? 'Solte um cartão aqui' : 'Nenhum credenciado nesta etapa'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={`Rejeitar ${rejecting?.name ?? 'credenciado'}`}
        footer={<>
          <Button variant="ghost" onClick={() => setRejecting(null)}>Cancelar</Button>
          <Button variant="danger" onClick={confirmReject} loading={rejectSaving}>Rejeitar</Button>
        </>}
      >
        <p className="text-sm text-gray-600 mb-3">Informe a justificativa da rejeição. Ela ficará registrada no credenciado.</p>
        <Textarea
          label="Justificativa"
          required
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={4}
          placeholder="Ex.: documentação incompleta, valores fora da tabela"
        />
      </Modal>

      <Modal
        open={!!transferring}
        onClose={() => setTransferring(null)}
        title={`Transferir ${transferring?.name ?? 'credenciado'}`}
        footer={<>
          <Button variant="ghost" onClick={() => setTransferring(null)}>Cancelar</Button>
          <Button onClick={confirmTransfer} loading={transferSaving} disabled={!transferUserId}>Transferir</Button>
        </>}
      >
        <p className="text-sm text-gray-600 mb-3">
          A titularidade do credenciado passa para o usuário escolhido, que assume o comissionamento deste credenciamento.
        </p>
        {transferring?.createdByName && (
          <p className="text-xs text-gray-500 mb-3">Titular atual: <strong className="text-gray-700">{transferring.createdByName}</strong></p>
        )}
        <Select
          label="Transferir para"
          required
          value={transferUserId}
          onChange={(e) => setTransferUserId(e.target.value)}
          options={transferUsers.map((u) => ({ value: u.id, label: u.name }))}
          placeholder="Selecione um administrador ou colaborador"
          searchPlaceholder="Buscar usuário"
        />
      </Modal>

      {view && (
        <ProviderViewModal
          providerId={view.id}
          providerName={view.name}
          mode={view.mode}
          onClose={() => setView(null)}
          onChanged={() => load(userFilter)}
        />
      )}

      <ConfirmDialog
        open={!!approving}
        onClose={() => setApproving(null)}
        onConfirm={confirmApprove}
        loading={approveSaving}
        title="Aceitar credenciado?"
        description={`Ao aceitar, "${approving?.name ?? ''}" passa para o status Aceito e sai do kanban. Esta ação registra a data de ativação.`}
        confirmLabel="Aceitar"
      />
    </>
  )
}

// =============================================================================
// Card
// =============================================================================
function KanbanCardView({ card, menuItems, draggable, onDragStart, onDragEnd, onApprove }: {
  card: KanbanCard
  menuItems: DropdownItem[]
  /** Sem permissão de mover etapa, o cartão não é arrastável. */
  draggable: boolean
  onDragStart: () => void
  onDragEnd: () => void
  /** Ausente = sem permissão de aceitar (o botão não aparece). */
  onApprove?: () => void
}) {
  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      className={cn(
        'bg-white rounded-md ring-1 ring-gray-200 p-3 hover:ring-brand-300 transition',
        draggable && 'cursor-grab active:cursor-grabbing',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-800 break-words">{card.name}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">{card.formatted?.document ?? card.document}</div>
        </div>
        <DropdownMenu items={menuItems} trigger={<MoreVertical size={16} />} ariaLabel="Ações do credenciado" />
      </div>

      {card.providerType && <div className="mt-2"><Badge variant="violet">{card.providerType}</Badge></div>}

      {/* Contatos */}
      {card.contacts.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {card.contacts.slice(0, 3).map(c => (
            <li key={c.id} className="flex items-center gap-1.5 text-[11px] text-gray-600">
              {c.type === 'E-MAIL' ? <Mail size={11} className="text-gray-400 shrink-0" /> : <PhoneIcon size={11} className="text-gray-400 shrink-0" />}
              <span className="break-words">{c.formatted?.contact ?? c.contact}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Rodapé: data + autor */}
      <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <CalendarDays size={10} /> {card.formatted?.createdAt ?? '—'}
        </div>
        {card.createdByName && (
          <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
            <UserCircle size={10} /> {card.createdByName}
          </div>
        )}
      </div>

      {/* Estado de completude */}
      <div className="mt-3">
        {card.isComplete ? (
          onApprove && (
            <Button size="xs" className="w-full" iconLeft={<ThumbsUp size={12} />} onClick={onApprove}>
              Aceitar
            </Button>
          )
        ) : (
          <InfoPopover
            align="left"
            ariaLabel="Ver informações pendentes"
            title="Informações pendentes"
            trigger={
              <span className="flex w-full items-center justify-center gap-1.5 rounded-md bg-amber-50 ring-1 ring-amber-200 px-2 py-1.5 text-[11px] font-medium text-amber-700 hover:bg-amber-100">
                <AlertTriangle size={12} className="shrink-0" />
                Faltam informações
              </span>
            }
          >
            <ul className="space-y-1 text-sm">
              {card.missing.map(m => (
                <li key={m} className="flex items-center gap-2 text-gray-700">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                  {m}
                </li>
              ))}
            </ul>
          </InfoPopover>
        )}
      </div>
    </div>
  )
}
